import React from 'react';
import { DataCollectionGapSummaryMaterial } from '../../../../store/Models/Api/DataCollectionGapSummaryModels';
import { useLocalizer } from '../hooks/useLocalizer';
import {
    formatMaterialDescription,
    formatMaterialName,
    getMaterialUnansweredCount,
    hasQuestionnaireGaps,
    hasRegulationGaps
} from './helpers/gapSummaryHelpers';
import QuestionnaireGapGroup from './QuestionnaireGapGroup';
import RegulationGapGroup from './RegulationGapGroup';

interface MaterialGapRowProps {
    material: DataCollectionGapSummaryMaterial;
    isExpanded: boolean;
    onToggle: (inventoryId: string) => void;
}

export const MaterialGapRow: React.FC<MaterialGapRowProps> = ({
    material,
    isExpanded,
    onToggle
}) => {
    const localize = useLocalizer();
    const unansweredCount = getMaterialUnansweredCount(material);
    const materialDescription = formatMaterialDescription(material);
    const regulationGaps = material.complianceRegulations?.filter(hasRegulationGaps) ?? [];
    const questionnaireGaps = material.customQuestionnaires?.filter(hasQuestionnaireGaps) ?? [];
    const unansweredRequiredBomColumns = material.bomDataValidation?.unansweredRequiredColumnNames ?? [];

    return (
        <div className="gap-summary-material-row">
            <button
                type="button"
                className="gap-summary-material-row-button"
                onClick={() => onToggle(material.inventoryId)}
                aria-expanded={isExpanded}
            >
                <span className={`glyphicon glyphicon-chevron-${isExpanded ? 'down' : 'right'}`} aria-hidden="true"></span>
                <span className="gap-summary-question-row-text">
                    <span className="gap-summary-question-row-name">{formatMaterialName(material)}</span>
                    {materialDescription && (
                        <span className="gap-summary-question-row-description">{materialDescription}</span>
                    )}
                </span>
                <span className="gap-summary-unanswered-pill">
                    {unansweredCount} {localize('Unanswered')}
                </span>
            </button>
            {isExpanded && (
                <div className="gap-summary-row-details">
                    {unansweredRequiredBomColumns.length > 0 && (
                        <div className="gap-summary-detail-section">
                            <div className="gap-summary-gap-group">
                                <div className="gap-summary-gap-group-title">
                                    <span>{localize('Ingredient Disclosure')}</span>
                                    <span className="gap-summary-gap-group-counts">
                                        0/{unansweredRequiredBomColumns.length} {localize('answered')}
                                    </span>
                                </div>
                                <ul className="gap-summary-question-list">
                                    {unansweredRequiredBomColumns.map(columnName => (
                                        <li key={columnName}>
                                            <span className="gap-summary-question-status"></span>
                                            <span>{localize(columnName)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                    {regulationGaps.length > 0 && (
                        <div className="gap-summary-detail-section">
                            {regulationGaps.map(regulation => (
                                <RegulationGapGroup
                                    key={regulation.id}
                                    regulation={regulation}
                                />
                            ))}
                        </div>
                    )}
                    {questionnaireGaps.length > 0 && (
                        <div className="gap-summary-detail-section">
                            {questionnaireGaps.map(questionnaire => (
                                <QuestionnaireGapGroup
                                    key={questionnaire.id}
                                    questionnaire={questionnaire}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MaterialGapRow;
